import { Injectable, inject } from '@angular/core';
import {
  CorePageModuleIDs,
  CoreRouterActionNavigateToPageModule,
  CoreRouterActions,
  CoreRouterNavigationIntent
} from '@bepatient/core';
import { ToastController } from '@ionic/angular';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import {
  catchError,
  map,
  switchMap,
  tap,
  withLatestFrom
} from 'rxjs/operators';
import { MessagesApiService } from './messages-api.service';
import {
  MessagesActionCreateDiscussionFailed,
  MessagesActionDownloadAndOpenFile,
  MessagesActionDownloadAndOpenFileFailed,
  MessagesActionDownloadAndOpenFileSuccess,
  MessagesActionLoadContactsFailed,
  MessagesActionLoadContactsSuccess,
  MessagesActionLoadDiscussionsFailed,
  MessagesActionLoadDiscussionsSuccess,
  MessagesActionLoadSelectedDiscussionFailed,
  MessagesActionLoadSelectedDiscussionSuccess,
  MessagesActions,
  MessagesActionSelectContact,
  MessagesActionSelectDiscussion,
  MessagesActionSendMessage,
  MessagesActionSendMessageFailed,
  MessagesActionSendMessageSuccess,
  MessagesActionsTypes
} from './messages.actions';
import { MessagesContact } from './messages.models';
import { selectorMessagesSelectedDiscussionUuid } from './messages.reducer';

@Injectable()
export class MessagesEffects {
  private store = inject<Store<any>>(Store);
  private actions$ = inject(Actions);
  private translate = inject(TranslateService);
  private toastController = inject(ToastController);
  private messagesApiService = inject(MessagesApiService);

  loadDiscussions$: Observable<MessagesActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(MessagesActionsTypes.LOAD_DISCUSSIONS),
      switchMap(() =>
        this.messagesApiService.loadDiscussions().pipe(
          map(res => new MessagesActionLoadDiscussionsSuccess(res)),
          catchError(() => of(new MessagesActionLoadDiscussionsFailed()))
        )
      )
    )
  );

  selectDiscussion$: Observable<CoreRouterActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(MessagesActionsTypes.SELECT_DISCUSSION),
      map(
        () =>
          new CoreRouterActionNavigateToPageModule({
            id: CorePageModuleIDs.MESSAGES,
            intent: CoreRouterNavigationIntent.FORWARD,
            pageIndex: 1
          })
      )
    )
  );

  loadDiscussion$: Observable<MessagesActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(MessagesActionsTypes.LOAD_SELECTED_DISCUSSION),
      withLatestFrom(this.store.select(selectorMessagesSelectedDiscussionUuid)),
      switchMap(([, selectedDiscussionUuid]) =>
        this.messagesApiService.loadDiscussion(selectedDiscussionUuid).pipe(
          map(res => new MessagesActionLoadSelectedDiscussionSuccess(res)),
          catchError(() => of(new MessagesActionLoadSelectedDiscussionFailed()))
        )
      )
    )
  );

  sendMessage$: Observable<MessagesActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(MessagesActionsTypes.SEND_MESSAGE),
      map((action: MessagesActionSendMessage) => action.payload),
      withLatestFrom(this.store.select(selectorMessagesSelectedDiscussionUuid)),
      switchMap(([message, selectedDiscussionUuid]) =>
        this.messagesApiService
          .sendMessage(selectedDiscussionUuid, message)
          .pipe(
            map(
              sentMessage => new MessagesActionSendMessageSuccess(sentMessage)
            ),
            catchError(() => of(new MessagesActionSendMessageFailed()))
          )
      )
    )
  );

  loadContacts$: Observable<MessagesActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(MessagesActionsTypes.LOAD_CONTACTS),
      switchMap(() =>
        this.messagesApiService.loadContacts().pipe(
          map(res => new MessagesActionLoadContactsSuccess(res)),
          catchError(() => of(new MessagesActionLoadContactsFailed()))
        )
      )
    )
  );

  selectContact$: Observable<MessagesActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(MessagesActionsTypes.SELECT_CONTACT),
      map((action: MessagesActionSelectContact) => action.payload),
      switchMap<
        MessagesContact,
        Observable<string | MessagesActionCreateDiscussionFailed>
      >(contact =>
        !contact.conversationHref
          ? this.messagesApiService
              .createDiscussion(contact.uuid)
              .pipe(
                catchError(() => of(new MessagesActionCreateDiscussionFailed()))
              )
          : of(contact.conversationHref)
      ),
      map<string | MessagesActionCreateDiscussionFailed, MessagesActions>(
        res => {
          return !(typeof res === 'string')
            ? (res as MessagesActionCreateDiscussionFailed)
            : new MessagesActionSelectDiscussion(
                /discussions\/(.*)$/.exec(res)[1]
              );
        }
      )
    )
  );

  displayToastError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          MessagesActionsTypes.LOAD_CONTACTS_FAILED,
          MessagesActionsTypes.LOAD_DISCUSSIONS_FAILED,
          MessagesActionsTypes.LOAD_SELECTED_DISCUSSION_FAILED,
          MessagesActionsTypes.SEND_MESSAGE_FAILED,
          MessagesActionsTypes.CREATE_DISCUSSION_FAILED,
          MessagesActionsTypes.DOWNLOAD_AND_OPEN_FILE_FAILED
        ),
        tap(() => {
          this.toastController
            .create({
              message: this.translate.instant('ERROR'),
              position: 'top',
              duration: 2000,
              cssClass: 'bepatient_dynamic_form_save_toast'
            })
            .then(toast => toast.present());
        })
      ),
    { dispatch: false }
  );

  downloadAndOpenFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MessagesActionsTypes.DOWNLOAD_AND_OPEN_FILE),
      map((action: MessagesActionDownloadAndOpenFile) => action.payload),
      switchMap(url => this.messagesApiService.downloadFile(url)),
      switchMap(res =>
        this.messagesApiService.openFile(res.body).pipe(
          map(() => new MessagesActionDownloadAndOpenFileSuccess()),
          catchError(() => of(new MessagesActionDownloadAndOpenFileFailed()))
        )
      )
    )
  );
}
